import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { downloadICS } from '../utils/icsHelper';
import {
  CalendarIcon, MapPinIcon, TrashIcon, ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon, PhoneIcon, EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/solid';
import { getCategoryColor } from '../utils/categoryColors';
import { createFormatEventDate } from '../utils/formatEventDate';
import EventModal from '../components/events/EventModal';

const TIER_COLORS = {
  'General': '#64748b', 'Gastronomy': '#ef4444', 'Shops': '#3b82f6',
  'Services': '#0f172a', 'Health': '#10b981', 'Wellness': '#ec4899',
  'Education': '#f59e0b', 'Technical': '#6366f1', 'Culture': '#8b5cf6',
};


  function HoursPreview({ summary, perDay }) {
    const [open, setOpen] = useState(false);
  
    return (
      <div className="text-sm">
        <button
          onClick={() => setOpen(s => !s)}
          className="text-sm text-left underline-offset-2 hover:underline"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          aria-expanded={open}
        >
          {summary}
        </button>
  
        {open && perDay && (
          <div className="mt-1 text-xs text-gray-700">
            {perDay.map(({ day, label }) => (
              <div key={day} className="flex gap-2">
                <div className="w-12 text-gray-500">{day.slice(0,3)}</div>
                <div>{label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }


const FavoritesPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const locale = user?.language || 'en-US';
  const formatEventDate = createFormatEventDate(locale);

  // 1. Initialize with empty arrays to be safe from the start
  const [data, setData] = useState({ providers: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [expandedProviders, setExpandedProviders] = useState(new Set());

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await api.get('/user/favorites');
      // 2. SAFETY CHECK: Ensure we save valid arrays, even if backend sends null/undefined

      // 1. Get the current timestamp
      const now = new Date();
  
      // 2. Filter events where the 'end' date is greater than 'now'
      const futureEvents = (res.data.events || []).filter(event => {
        const eventEndDate = new Date(event.end);
        return eventEndDate > now;
      });
        
      setData({
          providers: res.data.providers || [],
          events: futureEvents || []
      });
    } catch (err) {
      console.error("Error fetching dashboard data", err);
    } finally {
      setLoading(false);
    }
  };

  const removeEvent = async (id) => {
    if(!window.confirm(t('profile.favorites.alerts.remove-event'))) return;
    try {
        await api.put(`/user/event/${id}`);
        // Optimistic update: Remove from UI immediately
        setData(prev => ({ 
            ...prev, 
            events: prev.events.filter(e => e._id !== id) 
        }));
    } catch (err) { alert(t('general.alerts.action-error')); }
  };

  const removeProvider = async (id) => {
    if(!window.confirm(t('profile.favorites.alerts.unpin-place'))) return;
    try {
        await api.put(`/user/favorite/${id}`);
        // Optimistic update: Remove from UI immediately
        setData(prev => ({ 
            ...prev, 
            providers: prev.providers.filter(p => p._id !== id) 
        }));
    } catch (err) { alert(t('general.alerts.action-error')); }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen text-gray-500 font-medium animate-pulse">
        {t('general.loading')}
    </div>
  );

  // Helper variables for clean JSX (Safe access to length)
  const eventCount = data.events?.length || 0;
  const placeCount = data.providers?.length || 0;

  const openInGoogleMaps = (provider) => {
    // 1. Try Coordinates first (Most accurate)
    if (provider.location && provider.location.coordinates) {
        const [lng, lat] = provider.location.coordinates;
        window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
    } 
    // 2. Fallback to Address string if coords missing
    else if (provider.location.address) {
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(provider.location.address)}`, '_blank');
    } 
    else {
        alert(t('general.alerts.error-geolocation'));
    }
  };


  const normalizeKey = (str) => {
    if (!str) return '';
    return str.toString().toLowerCase().trim().replace(/\s+/g, '-');
  };
  

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      
      {/* --- DASHBOARD HEADER --- */}
      <div className="bg-gradient-to-r from-[#1d5cab] to-[#2372bd] rounded-2xl p-8 text-white shadow-xl mb-10 relative overflow-hidden">
        {/* Decorative circle for style */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white opacity-10 rounded-full"></div>
        
        <div className="relative z-10">
            <h1 className="text-3xl font-bold">{t('profile.favorites.welcome')} {user?.name || ''}!</h1>
            <p className="text-blue-100 mt-2 text-lg">{t('profile.favorites.subtitle')}</p>
            
            <div className="flex gap-6 mt-8">
                <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-xl border border-white/30 min-w-[140px]">
                    <span className="block text-3xl font-bold">{eventCount}</span>
                    <span className="text-sm text-[#a6c3db] font-medium">{t('profile.favorites.saved-events')}</span>
                </div>
                <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-xl border border-white/30 min-w-[140px]">
                    <span className="block text-3xl font-bold">{placeCount}</span>
                    <span className="text-sm text-[#a6c3db] font-medium">{t('profile.favorites.favorite-providers')}</span>
                </div>
            </div>
        </div>
      </div>

      {/* --- SECTION 1: UPCOMING EVENTS --- */}
      <div className="mb-12">
        <div className="flex justify-between items-end mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <CalendarIcon className="h-6 w-6 text-[#1d5cab]"/>
                {t('profile.favorites.my-events')}
            </h2>
            <Link to="/events" className="text-sm font-bold text-[#1d5cab] hover:underline">{t('profile.favorites.browse-events')} &rarr;</Link>
        </div>
        
        {eventCount === 0 ? (
             <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                 <p className="text-gray-500 mb-2">{t('profile.favorites.no-events')}</p>
                 <Link to="/events" className="bg-white border border-gray-300 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-50 text-gray-700">{t('profile.favorites.find-events')}</Link>
             </div>
        ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {/* SAFE MAP LOOP using optional chaining '?.' */}
                {data.events?.filter(e => new Date(e.end) > new Date()).map(e => (
                    <div key={e._id} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col relative group">
                        
                        {/* Remove Button (Visible on Hover) */}
                        <button 
                            onClick={() => removeEvent(e._id)} 
                            className="absolute top-4 right-4 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 bg-white rounded-full p-1 shadow-sm"
                            title="Unpin Event"
                        >
                            <TrashIcon className="h-5 w-5" />
                        </button>

                        <div className="mb-3">
                             <span
                      className="text-xs font-bold uppercase tracking-wide px-2 py-1 rounded"
                      style={{ backgroundColor: getCategoryColor(e.category).light,
                               color: getCategoryColor(e.category).primary
                      }}>
                        
                      {t(`events.event-management.event-type.${normalizeKey(e.category)}`,
                                          { defaultValue: e.category }
                      )}
                      
                            </span>
                        </div>
                        
                        <h3 className="font-bold text-lg text-gray-900 mb-1">{e.title}</h3>
                        
                        <div className="text-sm text-gray-500 mb-4 flex-grow">
                            <div className="flex items-start gap-2 text-xs text-gray-600">
                                    <CalendarIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                            <p className="mb-1 font-medium text-gray-700">
                                 {formatEventDate(e.start, e.end)}
                            </p></div>
                            <div className="flex items-start gap-2 text-xs text-gray-600">
                                    <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                                    <p className="mb-1 font-medium text-gray-700"> {e.provider.location?.address || e.provider?.location?.formattedAddress  ||  e.provider?.businessName || t('profile.favorites.unknown-venue')}</p>
                            </div>
                        </div>

                        {/* --- card footer with export button --- */}
                        <div className="border-t border-gray-100 pt-3 mt-auto flex justify-between items-center">
                            <button onClick={() => { setIsDescExpanded(false); setSelectedEvent(e); }} className="text-sm font-bold text-gray-600 hover:text-blue-600">
                                 {t('profile.favorites.view-details')}
                            </button>
                            
                            {/* Export button */}
                            <button 
                                onClick={() => downloadICS(e)}
                                className="flex items-center gap-1 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                                title="Add to Calendar (Outlook, Google, Apple)"
                            >
                                <ArrowDownTrayIcon className="h-3 w-3" />
                                 {t('profile.favorites.save-calendar')}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      {/* --- SECTION 2: FAVORITE PLACES --- */}
      <div>
        <div className="flex justify-between items-end mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <MapPinIcon className="h-6 w-6 text-[#fc7728]" />
                {t('profile.favorites.favorite-services')}
            </h2>
            <Link to="/map" className="text-sm font-bold text-[#1d5cab] hover:underline">{t('profile.favorites.explore-map')}&rarr;</Link>
        </div>
        
        {placeCount === 0 ? (
             <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                 <p className="text-gray-500 mb-2">{t('profile.favorites.no-places')}</p>
                 <Link to="/map" className="bg-white border border-gray-300 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-50 text-gray-700">{t('profile.favorites.go-to-map')}</Link>
             </div>
        ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {/* SAFE MAP LOOP using optional chaining '?.' */}

                {data.providers?.map(p => (
                    <div key={p._id} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 relative group flex flex-col h-full">
                    
                        {/* --- UNPIN BUTTON (Top Right) --- */}
                        <button 
                            onClick={() => removeProvider(p._id)} 
                            className="absolute top-4 right-4 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 bg-white rounded-full p-1 shadow-sm z-10 border border-gray-100"
                            title="Unpin Place"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>


                        <div className="mb-3">
                             <span
                      className="text-xs font-bold uppercase tracking-wide px-2 py-1 rounded"
                      style={{ backgroundColor: (TIER_COLORS[p.tier] || '#64748b') + '1a', color: TIER_COLORS[p.tier] || '#64748b' }}>

                      {t(`map.${normalizeKey(p.tier)}`,
                                          { defaultValue: p.tier }
                      )}

                            </span>
                        </div>

                        {/* --- HEADER --- */}
                        <div className="mb-2 pr-8">
                            <h3 className="font-bold text-gray-900 text-lg truncate">{p.businessName}</h3>
                            
                        </div>
                    
                        {p.details?.description && (() => {
                          const isExpanded = expandedProviders.has(p._id);
                          const needsClamp = p.details.description.length > 150;
                          return (
                            <div className="mb-4">
                              <p className={`text-sm text-gray-500 leading-relaxed ${!isExpanded && needsClamp ? 'line-clamp-3' : ''}`}>
                                {p.details.description}
                              </p>
                              {needsClamp && (
                                <button
                                  onClick={() => setExpandedProviders(prev => {
                                    const next = new Set(prev);
                                    isExpanded ? next.delete(p._id) : next.add(p._id);
                                    return next;
                                  })}
                                  className="text-xs font-bold text-blue-600 hover:text-blue-800 mt-1"
                                >
                                  {isExpanded ? t('hero.read-less') : t('hero.read')}
                                </button>
                              )}
                            </div>
                          );
                        })()}

                        {/* --- INFO LIST (Address, Phone, etc.) --- */}
                        <div className="space-y-2 mb-4 border-t border-gray-50 pt-3">
                            
                            {/* Address */}
                            {(p.location?.address || p.location?.formattedAddress) && (
                                <div className="flex items-start gap-2 text-xs text-gray-600">
                                    <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                                    <span className="leading-tight">{p.location.address || p.location.formattedAddress}</span>
                                </div>
                            )}

                            {/* Phone */}
                            {p.contact?.phone && (
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <PhoneIcon className="h-4 w-4 text-gray-400 shrink-0" />
                                    <a href={`tel:${p.contact.phone}`} className="hover:text-blue-600 transition-colors">
                                        {p.contact.phone}
                                    </a>
                                </div>
                            )}

                            {/* Email */}
                            {p.contact?.email && (
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <EnvelopeIcon className="h-4 w-4 text-gray-400 shrink-0" />
                                    <a href={`mailto:${p.contact.email}`} className="hover:text-blue-600 transition-colors truncate">
                                        {p.contact.email}
                                    </a>
                                </div>
                            )}

                            {/* Website */}
                            {p.details?.website && (
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <GlobeAltIcon className="h-4 w-4 text-gray-400 shrink-0" />
                                    <a href={p.details.website} className="hover:text-blue-600 transition-colors truncate">
                                        {t('profile.favorites.visit-website')}
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* --- FOOTER ACTIONS --- */}
                        {/* 'justify-end' pushes the Directions button to the right since View Profile is gone */}
                        <div className="border-t border-gray-100 pt-3 mt-auto flex justify-end items-center">
                            <button 
                                onClick={() => openInGoogleMaps(p)}
                                className="flex items-center gap-1.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                                title="Navigate with Google Maps"
                            >
                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                 {t('profile.favorites.open-route')}
                            </button>
                        </div>

                    </div>
                ))}
           </div>
        )}
      </div>

      <EventModal
        event={selectedEvent}
        formatEventDate={formatEventDate}
        normalizeKey={normalizeKey}
        savedEventIds={data.events?.map(e => e._id) || []}
        isDescExpanded={isDescExpanded}
        onToggleDesc={() => setIsDescExpanded(!isDescExpanded)}
        onClose={() => setSelectedEvent(null)}
        onToggleSave={(e, id) => { e.stopPropagation(); removeEvent(id); setSelectedEvent(null); }}
        t={t}
        i18nLanguage={locale}
      />
    </div>
  );
};

export default FavoritesPage;
